import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { HttpService } from './http.service';
import {
  CreateLinkTemplatePayload,
  LinkTemplate,
  LinkTemplateDetail,
  LinkTemplatesExportPayload,
  LinkTemplatesListMeta,
  LinkTemplatesListParams,
  UpdateLinkTemplatePayload,
} from '../../store/entities/models/link-template.model';
import { transformCamelToSnake, transformSnakeToCamel } from '../../store/utils/transformers';
import {
  GS1_IDENTIFIER_CODES,
  isGs1AttributeType,
} from '../../store/entities/models/attribute.model';

interface ApiResponse<T> {
  data: T;
  meta?: LinkTemplatesListMeta;
  message?: string;
  success?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LinkTemplatesService {
  private http = inject(HttpService);

  getTemplates(params: LinkTemplatesListParams = {}): Observable<{ data: LinkTemplate[]; meta: LinkTemplatesListMeta }> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? 1,
      per_page: params.perPage ?? 25,
      search: params.search ?? '',
      sort: params.sort ?? 'created_at',
      sort_direction: params.sortDirection ?? 'desc',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filterLink ?? 'and';
    }

    return this.http
      .get<Record<string, unknown>>('/templates', queryParams)
      .pipe(map((response) => transformSnakeToCamel(response) as { data: LinkTemplate[]; meta: LinkTemplatesListMeta }));
  }

  getTemplateById(uuid: string): Observable<LinkTemplateDetail> {
    return this.http
      .get<Record<string, unknown>>(`/templates/${uuid}`)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>;

          return (transformed.data ?? transformed) as LinkTemplateDetail;
        })
      );
  }

  createTemplate(payload: CreateLinkTemplatePayload): Observable<LinkTemplateDetail> {
    return this.http
      .post<Record<string, unknown>>('/templates', this.toApiPayload(payload))
      .pipe(map((response) => (transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>).data));
  }

  updateTemplate(uuid: string, payload: UpdateLinkTemplatePayload): Observable<LinkTemplateDetail> {
    return this.http
      .put<Record<string, unknown>>(`/templates/${uuid}`, this.toApiPayload(payload))
      .pipe(map((response) => (transformSnakeToCamel(response) as ApiResponse<LinkTemplateDetail>).data));
  }

  deleteTemplate(uuid: string): Observable<void> {
    return this.http.delete<void>(`/templates/${uuid}`);
  }

  exportTemplates(payload: LinkTemplatesExportPayload): Observable<{ message?: string }> {
    return this.http
      .post<Record<string, unknown>>('/templates/export', transformCamelToSnake(payload))
      .pipe(map((response) => transformSnakeToCamel(response) as { message?: string }));
  }

  setDefaultTemplate(accountUuid: string, templateUuid: string): Observable<unknown> {
    return this.http.patch(
      `/tenants/${accountUuid}`,
      transformCamelToSnake({ defaultLinkTemplateUuid: templateUuid })
    );
  }

  /**
   * Get a download URL for a template export file.
   *
   * @param uuid - Template UUID
   * @returns Observable of the download URL string
   *
   * API: GET /templates/:uuid/export?format=xls
   * Response: { data: { file_path: string } }
   *
   * Throws an explicit error if file_path is absent or null in the response
   * so that the subscriber's error handler fires rather than silently calling
   * window.open(undefined, '_self'). [NIT-6 resolved]
   */
  getTemplateExportUrl(uuid: string): Observable<string> {
    return this.http
      .get<{ data: { file_path: string | null } }>(`/templates/${uuid}/export`, { format: 'xls' })
      .pipe(
        map((res) => {
          const url = res.data?.file_path;

          if (!url) {
            throw new Error('Template download URL not found in response');
          }

          return url;
        }),
      );
  }

  private toApiPayload(payload: CreateLinkTemplatePayload | UpdateLinkTemplatePayload): Record<string, unknown> {
    // Everything except `attributes` gets converted to snake_case. The
    // attribute objects must be sent in the same mixed shape the React app
    // sends (descriptive keys stay camelCase, e.g. `fieldType`/`isGS1`, while
    // the assignment keys are snake_case, e.g. `is_assigned`). The backend
    // reads `fieldType` directly, so snake_casing the attributes would drop it
    // and trigger `Undefined array key "fieldType"`.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attributes, ...rest } = payload;

    const request = {
      ...rest,
      groups: payload.groups.map((uuid) => ({ uuid })),
      tags: payload.tags.map((id) => ({ id })),
      availableQrTemplates: payload.availableQrTemplates.map((uuid) => ({ uuid })),
    };

    if (!request.retentionType || !request.retentionDays || request.retentionDays <= 0) {
      delete request.retentionType;
      delete request.retentionDays;
    }

    const body = transformCamelToSnake(request);

    body.attributes = this.toApiAttributes(payload);

    return body;
  }

  /**
   * Builds the `attributes` array to match the React LinkTemplateForm payload.
   *
   * - Filter: send every non-GS1 attribute (assigned or not) so the backend can
   *   reconcile the full set; only GS1 identifiers / GS1-template attributes are
   *   restricted to assigned ones.
   * - Shape: keep descriptive keys camelCase (the backend reads `fieldType`) and
   *   emit the assignment/order keys in snake_case.
   */
  private toApiAttributes(
    payload: CreateLinkTemplatePayload | UpdateLinkTemplatePayload,
  ): Record<string, unknown>[] {
    return payload.attributes
      .filter((attribute) => {
        if (
          (GS1_IDENTIFIER_CODES as readonly string[]).includes(attribute.code)
        ) {
          return attribute.isAssigned;
        }

        if (payload.isGs1Template && isGs1AttributeType(attribute.type)) {
          return attribute.isAssigned;
        }

        return true;
      })
      .map((attribute) => {
        const {
          isAssigned,
          isRequired,
          isHidden,
          templateOrder,
          ...rest
        } = attribute;

        return {
          ...rest,
          value: attribute.value ?? null,
          template_order: templateOrder,
          is_assigned: isAssigned,
          is_hidden: isHidden,
          is_required: isRequired,
        };
      });
  }
}
